import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { collection, doc } from 'firebase/firestore';

import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import EventEditor from '../components/event/EventEditor';
import EventReviewAndFinalize from '../components/event/EventReviewAndFinalize';
import { ActionProps, useApp } from '../contextProviders/AppProvider';
import { useData } from '../contextProviders/DataProvider';
import useLeague from '../dataHooks/useLeague';
import useOrg from '../dataHooks/useOrg';
import useSeasons from '../dataHooks/useSeasons';
import { useEventMutators } from '../dataMutators/useEventMutators';
import { firestore } from '../firebase';
import DefaultObjects from '../types/DefaultObjects';
import Event from '../types/Event';
import Person from '../types/Person';

type Step = 'edit' | 'review';

export default function EventCreatePage(): ReactElement {
  const { orgId, leagueId, seasonId } = useParams();

  const { setPageName } = useApp();
  const { getAdminLeagues, getAdminOrgs, isLeagueAdmin, isOrgAdmin, userState } = useData();
  const { addEvent } = useEventMutators();
  const navigate = useNavigate();
  const [pageActions, setPageActions] = useState<ActionProps[] | null>(null);
  const [step, setStep] = useState<Step>('edit');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setPageName(step === 'edit' ? 'Create event' : 'Review and publish event');
  }, [setPageName, step]);

  // Handle browser back button - go back to edit step instead of leaving page
  useEffect(() => {
    if (step !== 'review') return;

    // Push a history entry when entering review step
    window.history.pushState({ step: 'review' }, '');

    const handlePopState = () => {
      setStep('edit');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step]);

  const validateUser = (userProfile: Person | null | undefined): boolean => {
    if (userProfile) {
      if (orgId) {
        if (leagueId) {
          return (isLeagueAdmin(leagueId, orgId)) ?? false;
        } else {
          return (isOrgAdmin(orgId)) ?? false;
        }
      } else {
        return getAdminOrgs().length > 0 || getAdminLeagues().length > 0 ? true : false;
      }
    } else {
      return false;
    }
  };

  // Pre-generate the document ID so we can use it for the logo path before saving
  const initialEvent = useMemo((): Event => {
    const eventOrgId = orgId ?? '';
    // Generate doc ID - if no orgId yet, use a placeholder path (will be updated when league is selected)
    const eventRef = eventOrgId
      ? doc(collection(firestore, `org/${eventOrgId}/event`))
      : doc(collection(firestore, 'org/placeholder/event'));

    return {
      ...DefaultObjects.Event,
      itemId: eventRef.id,
      creatorId: userState.userProfile?.itemId ?? '',
      leagueId: leagueId ?? '',
      seasonId: seasonId ?? '',
      orgId: eventOrgId,
    };
  }, [orgId, leagueId, seasonId, userState.userProfile?.itemId]);

  // Event state for reactive preview updates
  const [event, setEvent] = useState<Event>(initialEvent);

  // Update event state when initialEvent changes (e.g., when URL params change)
  useEffect(() => {
    setEvent(initialEvent);
  }, [initialEvent]);

  // Fetch league, org, and seasons for defaults (use event state to track as user changes league)
  const { league } = useLeague({ orgId: event.orgId || undefined, leagueId: event.leagueId || undefined });
  const { org } = useOrg(event.orgId || undefined);
  const { seasons } = useSeasons({ orgId: event.orgId || undefined, leagueId: event.leagueId || undefined });

  const handleCancel = useCallback(() => {
    // If in review step, go back 2 entries (one for pushed review entry, one to get back to origin)
    // If in edit step, go back 1 entry
    navigate(step === 'review' ? -2 : -1);
  }, [navigate, step]);

  const handleReview = useCallback((updatedEvent: Event) => {
    setEvent(updatedEvent);
    setStep('review');
  }, []);

  const handleFinalize = useCallback(() => {
    if (event) {
      addEvent(event);
    }
    // Navigate back 2 entries: one for the review step history entry we pushed, one to get back to origin
    navigate(-2);
  }, [event, addEvent, navigate]);

  // Store callbacks in refs to avoid infinite loops in useEffect
  const handleCancelRef = useRef(handleCancel);
  const handleFinalizeRef = useRef(handleFinalize);

  useEffect(() => {
    handleCancelRef.current = handleCancel;
    handleFinalizeRef.current = handleFinalize;
  });

  // Handle page actions from EventEditor (for edit step)
  const handleActionsChange = useCallback((actions: ActionProps[] | null) => {
    if (step === 'edit') {
      setPageActions(actions);
    }
  }, [step]);

  // Build page actions for review step
  useEffect(() => {
    if (step !== 'review') return;

    setPageActions([
      { name: 'Publish', icon: CheckIcon, handleClick: () => handleFinalizeRef.current(), variant: 'contained', color: 'primary' },
    ]);
  }, [step]);

  return (
    <GratiPageWrapper isAuthRequired={true} validateUser={validateUser} pageActions={pageActions}>
      {step === 'edit' ? (
        <EventEditor
          event={event}
          league={league ?? undefined}
          org={org ?? undefined}
          seasons={seasons ?? undefined}
          isNewEvent={true}
          restrictToOrgId={orgId}
          handleCancel={handleCancel}
          handleSubmit={handleReview}
          onActionsChange={handleActionsChange}
          onEventChange={setEvent}
        />
      ) : (
        <EventReviewAndFinalize
          event={event}
          league={league ?? undefined}
          isEditing={isEditing}
          onEventChange={setEvent}
          onEditingClose={() => setIsEditing(false)}
          onEditingOpen={() => setIsEditing(true)}
        />
      )}
    </GratiPageWrapper>
  );
}
